import type { LoadedFont } from '../fonts/fontLoader';
import type { Param, ParamSchema, ParamValues } from '../params/types';
import { DEFAULT_TEXT_SETTINGS, type TextSettings } from './settings';

/**
 * The text settings expressed as a parameter schema.
 *
 * Going through the same declarative system as the techniques buys three things at
 * once: the sliders inherit lock, randomise and exploration-range behaviour; the batch
 * engine can sweep font size, weight and tracking without any special-casing; and
 * presets round-trip through one code path.
 *
 * The schema is *per font*, because a variable font contributes one slider per axis
 * from its `fvar` table. `buildTextSchema(font)` is memoised on the font id.
 *
 * A few controls stay bespoke and are marked `neverRandomise` here, with their own UI
 * in the text panel: the font picker (needs live previews and upload), the anchor grid
 * (a 3×3 pad, not a slider) and the text content itself.
 */

const D = DEFAULT_TEXT_SETTINGS;

const BASE_TEXT_SCHEMA = [
  {
    kind: 'text',
    key: 'text',
    label: 'Textinhalt',
    default: D.text,
    multiline: true,
    placeholder: 'Wort oder mehrere Zeilen…',
    group: 'Text',
    tooltip: 'Der zu setzende Text. Zeilenumbrüche werden übernommen.',
    bulkValues: ['FORM', 'FELD', 'WACHSTUM', 'RAUSCHEN', 'STRUKTUR', 'ZERFALL'],
  },
  {
    kind: 'enum',
    key: 'transform',
    label: 'Transform',
    default: D.transform,
    segmented: true,
    group: 'Text',
    tooltip: 'Gross- und Kleinschreibung vor dem Setzen anwenden.',
    options: [
      { value: 'none', label: 'Aa' },
      { value: 'upper', label: 'AA' },
      { value: 'lower', label: 'aa' },
      { value: 'smallcaps', label: 'Aᴀ' },
    ],
  },
  {
    kind: 'float',
    key: 'smallCapsScale',
    label: 'Kapitälchen-Höhe',
    min: 0.4,
    max: 1,
    step: 0.01,
    default: D.smallCapsScale,
    group: 'Text',
    tooltip: 'Grösse der verkleinerten Versalien im Verhältnis zur vollen Höhe.',
    showIf: (v) => v.transform === 'smallcaps',
  },

  // ── Grösse & Gewicht ──────────────────────────────────────────────────────
  {
    kind: 'bool',
    key: 'autoFit',
    label: 'Auto-Fit',
    default: D.autoFit,
    group: 'Grösse & Gewicht',
    tooltip: 'Skaliert den Text automatisch auf einen Anteil der Canvas-Grösse.',
  },
  {
    kind: 'float',
    key: 'autoFitPercent',
    label: 'Fit-Breite',
    min: 0.05,
    max: 1.5,
    step: 0.005,
    default: D.autoFitPercent,
    unit: '×',
    bulkRange: [0.4, 1.0],
    group: 'Grösse & Gewicht',
    tooltip: 'Anteil der Canvas-Breite, den der breiteste Zeilenblock einnimmt.',
    showIf: (v) => v.autoFit === true,
  },
  {
    kind: 'float',
    key: 'fontSize',
    label: 'Schriftgrösse',
    min: 8,
    max: 2000,
    step: 1,
    scale: 'log',
    default: D.fontSize,
    unit: 'px',
    bulkRange: [120, 900],
    group: 'Grösse & Gewicht',
    showIf: (v) => v.autoFit !== true,
  },
  {
    kind: 'int',
    key: 'weight',
    label: 'Gewicht',
    min: 100,
    max: 900,
    step: 1,
    default: D.weight,
    group: 'Grösse & Gewicht',
    tooltip: 'Bedient die wght-Achse, sofern die Schrift eine hat.',
  },

  // ── Satz ──────────────────────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'tracking',
    label: 'Laufweite',
    min: -0.5,
    max: 2,
    step: 0.001,
    default: D.tracking,
    unit: 'em',
    precision: 3,
    bulkRange: [-0.08, 0.5],
    group: 'Satz',
    tooltip: 'Abstand zwischen allen Zeichen, in em.',
  },
  {
    kind: 'float',
    key: 'wordSpacing',
    label: 'Wortabstand',
    min: -0.5,
    max: 3,
    step: 0.01,
    default: D.wordSpacing,
    unit: 'em',
    bulkRange: [0, 1],
    group: 'Satz',
    tooltip: 'Zusätzlicher Abstand an Leerzeichen, in em.',
  },
  {
    kind: 'float',
    key: 'lineHeight',
    label: 'Zeilenhöhe',
    min: 0.5,
    max: 3,
    step: 0.01,
    default: D.lineHeight,
    unit: '×',
    bulkRange: [0.75, 1.6],
    group: 'Satz',
  },
  {
    kind: 'enum',
    key: 'align',
    label: 'Ausrichtung',
    default: D.align,
    segmented: true,
    group: 'Satz',
    options: [
      { value: 'left', label: 'Links' },
      { value: 'center', label: 'Mitte' },
      { value: 'right', label: 'Rechts' },
      { value: 'justify', label: 'Block' },
    ],
  },

  // ── Position & Verzerrung ─────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'offsetX',
    label: 'Versatz X',
    min: -0.5,
    max: 0.5,
    step: 0.001,
    default: D.offsetX,
    precision: 3,
    bulkRange: [-0.15, 0.15],
    group: 'Position & Verzerrung',
    tooltip: 'Anteil der Canvas-Breite — bleibt beim Formatwechsel gültig.',
  },
  {
    kind: 'float',
    key: 'offsetY',
    label: 'Versatz Y',
    min: -0.5,
    max: 0.5,
    step: 0.001,
    default: D.offsetY,
    precision: 3,
    bulkRange: [-0.15, 0.15],
    group: 'Position & Verzerrung',
  },
  {
    kind: 'float',
    key: 'rotation',
    label: 'Rotation',
    min: -180,
    max: 180,
    step: 0.5,
    default: D.rotation,
    unit: '°',
    bulkRange: [-25, 25],
    group: 'Position & Verzerrung',
  },
  {
    kind: 'float',
    key: 'skewX',
    label: 'Neigung X',
    min: -60,
    max: 60,
    step: 0.5,
    default: D.skewX,
    unit: '°',
    bulkRange: [-20, 20],
    group: 'Position & Verzerrung',
  },
  {
    kind: 'float',
    key: 'skewY',
    label: 'Neigung Y',
    min: -60,
    max: 60,
    step: 0.5,
    default: D.skewY,
    unit: '°',
    bulkRange: [-12, 12],
    group: 'Position & Verzerrung',
  },

  // ── Grundlinie ────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'baselineMode',
    label: 'Verlauf',
    default: D.baseline.mode,
    segmented: true,
    group: 'Grundlinie',
    options: [
      { value: 'straight', label: 'Gerade' },
      { value: 'arc', label: 'Bogen' },
      { value: 'wave', label: 'Welle' },
    ],
  },
  {
    kind: 'float',
    key: 'arcRadius',
    label: 'Radius',
    min: -3000,
    max: 3000,
    step: 5,
    default: D.baseline.arcRadius,
    unit: 'px',
    bulkRange: [-2000, 2000],
    group: 'Grundlinie',
    tooltip: 'Positiv wölbt nach oben, negativ nach unten. Werte unter |1| sind gerade.',
    showIf: (v) => v.baselineMode === 'arc',
  },
  {
    kind: 'float',
    key: 'arcOffset',
    label: 'Startwinkel',
    min: -180,
    max: 180,
    step: 1,
    default: D.baseline.arcOffset,
    unit: '°',
    group: 'Grundlinie',
    showIf: (v) => v.baselineMode === 'arc',
  },
  {
    kind: 'float',
    key: 'waveAmplitude',
    label: 'Amplitude',
    min: 0,
    max: 600,
    step: 1,
    default: D.baseline.waveAmplitude,
    unit: 'px',
    bulkRange: [10, 220],
    group: 'Grundlinie',
    showIf: (v) => v.baselineMode === 'wave',
  },
  {
    kind: 'float',
    key: 'waveFrequency',
    label: 'Frequenz',
    min: 0,
    max: 20,
    step: 0.05,
    default: D.baseline.waveFrequency,
    bulkRange: [0.5, 6],
    group: 'Grundlinie',
    tooltip: 'Wellen pro 1000 px Grundlinie.',
    showIf: (v) => v.baselineMode === 'wave',
  },
  {
    kind: 'float',
    key: 'wavePhase',
    label: 'Phase',
    min: -180,
    max: 180,
    step: 1,
    default: D.baseline.wavePhase,
    unit: '°',
    group: 'Grundlinie',
    showIf: (v) => v.baselineMode === 'wave',
  },
  {
    kind: 'bool',
    key: 'followTangent',
    label: 'Buchstaben folgen der Kurve',
    default: D.baseline.followTangent,
    group: 'Grundlinie',
    tooltip: 'Aus: Buchstaben bleiben aufrecht und werden nur versetzt.',
    showIf: (v) => v.baselineMode !== 'straight',
  },

  // ── Feld ──────────────────────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'dilate',
    label: 'Dilate / Erode',
    min: -20,
    max: 50,
    step: 0.5,
    default: D.dilate,
    unit: 'px',
    bulkRange: [-6, 30],
    group: 'Feld',
    tooltip:
      'Verschiebt nur das Distanzfeld: wie viel Abstand generative Elemente zum Text halten. Die gezeichneten Buchstaben bleiben unverändert.',
  },
  {
    kind: 'float',
    key: 'outlineTolerance',
    label: 'Konturauflösung',
    min: 0.02,
    max: 5,
    step: 0.01,
    scale: 'log',
    default: D.outlineTolerance,
    unit: 'px',
    neverRandomise: true,
    group: 'Feld',
    tooltip: 'Maximale Abweichung beim Flachklopfen der Bézier-Kurven. Klein = glatt und langsam.',
  },
] as const satisfies ParamSchema;

/** Extra sliders for whatever variable axes the loaded font exposes, except `wght`. */
function axisParams(font: LoadedFont | null): Param[] {
  if (!font) return [];
  return font.axes
    .filter((axis) => axis.tag !== 'wght')
    .map((axis): Param => {
      const span = axis.max - axis.min;
      return {
        kind: 'float',
        key: `axis:${axis.tag}`,
        label: `${axis.name} (${axis.tag})`,
        min: axis.min,
        max: axis.max,
        step: span < 10 ? 0.01 : span < 200 ? 0.5 : 1,
        default: axis.default,
        group: 'Grösse & Gewicht',
        tooltip: `Variable Achse ${axis.tag}: ${axis.min} bis ${axis.max}.`,
      };
    });
}

const schemaCache = new Map<string, ParamSchema>();

export function buildTextSchema(font: LoadedFont | null): ParamSchema {
  const key = font ? `${font.id}:${font.axes.map((a) => a.tag).join(',')}` : '∅';
  const cached = schemaCache.get(key);
  if (cached) return cached;

  const axes = axisParams(font);
  // Axis sliders belong right after the weight control, inside the same group.
  const schema: Param[] = [];
  for (const p of BASE_TEXT_SCHEMA as readonly Param[]) {
    schema.push(p);
    if (p.key === 'weight') schema.push(...axes);
  }
  schemaCache.set(key, schema);
  return schema;
}

/** Base schema without any font-specific axes — enough for presets and batch setup. */
export const TEXT_SCHEMA: ParamSchema = BASE_TEXT_SCHEMA as readonly Param[];

// ── Adapters between flat parameter values and the typed TextSettings ────────

/**
 * Builds the typed `TextSettings` the layout engine consumes.
 *
 * The engine keeps its nested, strongly typed shape; only the UI and batch layers deal
 * in flat parameter values. Anything the schema does not cover (font id, anchor,
 * render mode) is passed through from the settings that own it.
 */
export function toTextSettings(
  values: ParamValues,
  extras: Pick<TextSettings, 'fontId' | 'anchorX' | 'anchorY' | 'visible' | 'renderMode' | 'strokeWidth'>,
): TextSettings {
  const num = (key: string, fallback: number): number => {
    const v = values[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  };
  const str = <T extends string>(key: string, fallback: T): T => {
    const v = values[key];
    return typeof v === 'string' ? (v as T) : fallback;
  };
  const bool = (key: string, fallback: boolean): boolean => {
    const v = values[key];
    return typeof v === 'boolean' ? v : fallback;
  };

  const variation: Record<string, number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (key.startsWith('axis:') && typeof value === 'number') variation[key.slice(5)] = value;
  }

  return {
    text: str('text', D.text),
    fontId: extras.fontId,
    fontSize: num('fontSize', D.fontSize),
    autoFit: bool('autoFit', D.autoFit),
    autoFitPercent: num('autoFitPercent', D.autoFitPercent),
    weight: num('weight', D.weight),
    variation,
    tracking: num('tracking', D.tracking),
    wordSpacing: num('wordSpacing', D.wordSpacing),
    lineHeight: num('lineHeight', D.lineHeight),
    align: str('align', D.align),
    anchorX: extras.anchorX,
    anchorY: extras.anchorY,
    offsetX: num('offsetX', D.offsetX),
    offsetY: num('offsetY', D.offsetY),
    rotation: num('rotation', D.rotation),
    skewX: num('skewX', D.skewX),
    skewY: num('skewY', D.skewY),
    transform: str('transform', D.transform),
    smallCapsScale: num('smallCapsScale', D.smallCapsScale),
    baseline: {
      mode: str('baselineMode', D.baseline.mode),
      arcRadius: num('arcRadius', D.baseline.arcRadius),
      arcOffset: num('arcOffset', D.baseline.arcOffset),
      waveAmplitude: num('waveAmplitude', D.baseline.waveAmplitude),
      waveFrequency: num('waveFrequency', D.baseline.waveFrequency),
      wavePhase: num('wavePhase', D.baseline.wavePhase),
      followTangent: bool('followTangent', D.baseline.followTangent),
    },
    outlineTolerance: num('outlineTolerance', D.outlineTolerance),
    visible: extras.visible,
    renderMode: extras.renderMode,
    strokeWidth: extras.strokeWidth,
    dilate: num('dilate', D.dilate),
  };
}
